import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { sitesApi } from "../lib/sites";
import UsageSummaryTable from "../components/usage/UsageSummaryTable";
import UsageDetailsTable from "../components/usage/UsageDetailsTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usageApi, type UsageDetailsParams } from "../lib/usage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, Filter } from "lucide-react";
import { format } from "date-fns";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { groupsApi } from "../lib/groups";
import { usersApi } from "../lib/users";

function MultiSelectTriggerLabel({
  placeholder,
  allLabel,
  selectedCount,
}: {
  placeholder: string;
  allLabel: string;
  selectedCount: number;
}) {
  return (
    <span className="truncate">
      {selectedCount > 0 ? `${placeholder} (${selectedCount})` : allLabel}
    </span>
  );
}

export default function UsagePage() {
  const [activeTab, setActiveTab] = useState<"summary" | "data">("summary");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [isExportingCsv, setIsExportingCsv] = useState(false);

  // Default to last 7 days (shared across tabs)
  const getDefaultStartDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const getDefaultEndDate = () => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date;
  };

  const [startDate, setStartDate] = useState<Date>(getDefaultStartDate());
  const [endDate, setEndDate] = useState<Date>(getDefaultEndDate());

  // Shared filters across Summary + Data tabs (union of both tabs' filter sets)
  const [filters, setFilters] = useState<Partial<UsageDetailsParams>>({});
  const [displayMode, setDisplayMode] = useState<"time" | "percent">("time");

  const updateFilter = (key: keyof UsageDetailsParams, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const parseMulti = (value: string | undefined): string[] =>
    (value ?? "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

  const setMulti = (
    key: keyof Pick<UsageDetailsParams, "areaName" | "presetName" | "groupName" | "userName">,
    nextValues: string[]
  ) => {
    const normalized = nextValues.map((v) => v.trim()).filter(Boolean);
    setFilters((prev) => ({
      ...prev,
      [key]: normalized.length > 0 ? normalized.join(",") : undefined,
    }));
  };

  const toggleMultiValue = (
    key: keyof Pick<UsageDetailsParams, "areaName" | "presetName" | "groupName" | "userName">,
    value: string
  ) => {
    const existing = new Set(parseMulti(filters[key] as string | undefined));
    if (existing.has(value)) {
      existing.delete(value);
    } else {
      existing.add(value);
    }
    setMulti(key, [...existing]);
  };

  const selectedAreaNames = parseMulti(filters.areaName);
  const selectedPresetNames = parseMulti(filters.presetName);
  const selectedGroupNames = parseMulti(filters.groupName);
  const selectedUserNames = parseMulti(filters.userName);

  const clearFilters = () => {
    setFilters({});
  };

  const handleQuickSelect = (period: string) => {
    const now = new Date();
    let start: Date;
    let end: Date;

    switch (period) {
      case "1d":
        start = new Date(now);
        start.setDate(start.getDate() - 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "7d":
        start = new Date(now);
        start.setDate(start.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        end = new Date(now);
        end.setHours(23, 59, 59, 999);
        break;
      case "month":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        end.setHours(23, 59, 59, 999);
        break;
      case "year":
        start = new Date(now.getFullYear(), 0, 1);
        start.setHours(0, 0, 0, 0);
        end = new Date(now.getFullYear(), 11, 31);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        return;
    }

    setStartDate(start);
    setEndDate(end);
  };

  const dateRangeParams = useMemo(() => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, [startDate, endDate]);

  // Fetch all sites the user has access to (backend filters based on role)
  const { data: sites, isLoading: sitesLoading } = useQuery({
    queryKey: ["sites"],
    queryFn: () => sitesApi.getAll(),
  });

  const { data: groups } = useQuery({
    queryKey: ["groups", filters.siteName],
    queryFn: async () => {
      const all = await groupsApi.getAll();
      if (!filters.siteName) return all;
      const site = sites?.find((s) => s.name === filters.siteName);
      if (!site) return all;
      return all.filter((g) => g.siteId === site.id);
    },
    enabled: true,
    staleTime: 60_000,
  });

  const { data: users } = useQuery({
    queryKey: ["users", filters.siteName],
    queryFn: async () => {
      const all = await usersApi.getAll();
      if (!filters.siteName) return all;
      return all.filter((u) => (u.sites ?? []).some((s) => s.name === filters.siteName));
    },
    enabled: true,
    staleTime: 60_000,
  });

  const { data: usageNameOptions } = useQuery({
    queryKey: ["usage-name-options", dateRangeParams, filters.siteName],
    queryFn: async () => {
      const limit = 200;
      const first = await usageApi.getDetails({
        ...dateRangeParams,
        ...(filters.siteName ? { siteName: filters.siteName } : {}),
        page: 1,
        limit,
        sortBy: "startTime",
        sortOrder: "desc",
      });

      const maxPages = Math.min(first.totalPages || 1, 10);
      const rows = [...(first.data ?? [])];
      for (let p = 2; p <= maxPages; p += 1) {
        const pageData = await usageApi.getDetails({
          ...dateRangeParams,
          ...(filters.siteName ? { siteName: filters.siteName } : {}),
          page: p,
          limit,
          sortBy: "startTime",
          sortOrder: "desc",
        });
        rows.push(...(pageData.data ?? []));
      }

      const areas = new Set<string>();
      const presets = new Set<string>();
      for (const r of rows) {
        if (r.areaName) areas.add(r.areaName);
        if (r.presetName) presets.add(r.presetName);
      }

      return {
        areaNames: [...areas].sort((a, b) => a.localeCompare(b)),
        presetNames: [...presets].sort((a, b) => a.localeCompare(b)),
      };
    },
    staleTime: 60_000,
  });

  const exportCsv = async () => {
    if (isExportingCsv) return;
    setIsExportingCsv(true);
    try {
      const limit = 100; // backend limit max
      const first = await usageApi.getDetails({
        ...dateRangeParams,
        ...filters,
        page: 1,
        limit,
        sortBy: "startTime",
        sortOrder: "desc",
      });

      const rows = [...(first.data ?? [])];
      const totalPages = first.totalPages || 1;
      const maxPages = Math.min(totalPages, 200);

      for (let p = 2; p <= maxPages; p += 1) {
        const pageData = await usageApi.getDetails({
          ...dateRangeParams,
          ...filters,
          page: p,
          limit,
          sortBy: "startTime",
          sortOrder: "desc",
        });
        rows.push(...(pageData.data ?? []));
      }

      const escapeCsv = (value: unknown): string => {
        const s = value == null ? "" : String(value);
        if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
        return s;
      };

      const formatDateTime = (iso: string | null | undefined): string =>
        iso ? format(new Date(iso), "yyyy-MM-dd HH:mm:ss") : "";

      const durationSeconds = (start: string, end: string | null | undefined, active: boolean): number => {
        const startMs = new Date(start).getTime();
        const endMs = end ? new Date(end).getTime() : active ? Date.now() : NaN;
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return 0;
        return Math.max(0, Math.floor((endMs - startMs) / 1000));
      };

      const headers = [
        "Site",
        "Area",
        "Preset",
        "Group",
        "User",
        "Start Time",
        "End Time",
        "Active",
        "Duration Seconds",
      ];

      const lines = [
        headers.map(escapeCsv).join(","),
        ...rows.map((r) => {
          const user = r.userName || r.userEmail || "";
          const seconds = durationSeconds(r.startTime, r.endTime, r.active);
          return [
            r.siteName ?? "",
            r.areaName ?? "",
            r.presetName ?? "",
            r.groupName ?? "",
            user,
            formatDateTime(r.startTime),
            formatDateTime(r.endTime),
            r.active ? "true" : "false",
            seconds,
          ]
            .map(escapeCsv)
            .join(",");
        }),
      ].join("\n");

      const blob = new Blob([lines], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `usage-details_${format(startDate, "yyyyMMdd")}-${format(endDate, "yyyyMMdd")}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setIsExportingCsv(false);
    }
  };

  return (
    <div className="px-2 py-3 sm:px-0 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold font-heading">Usage</h1>
        <p className="text-sm text-muted-foreground mt-1">
          History of area preset usage.
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as "summary" | "data")}
        className="w-full"
      >
        <Card>
          <CardHeader className="space-y-3">
            {/* Shared filters (persist across tabs) */}
            <div className="flex items-center justify-between gap-2">
              {/* Mobile: filter popover */}
              <div className="sm:hidden w-full">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full justify-between"
                    >
                      <span className="flex items-center">
                        <Filter className="mr-2 h-4 w-4" />
                        Filters
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {format(startDate, "PP")} → {format(endDate, "PP")}
                      </span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[280px] max-w-[calc(100vw-2rem)] sm:w-80"
                    align="start"
                    side="bottom"
                  >
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label className="text-xs">Start</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal text-sm"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                                <span className="truncate">
                                  {format(startDate, "PP")}
                                </span>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={startDate}
                                onSelect={(d) => d && setStartDate(d)}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs">End</Label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                className="w-full justify-start text-left font-normal text-sm"
                              >
                                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                                <span className="truncate">
                                  {format(endDate, "PP")}
                                </span>
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0">
                              <Calendar
                                mode="single"
                                selected={endDate}
                                onSelect={(d) => d && setEndDate(d)}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <Label className="text-xs">Quick</Label>
                          <Select onValueChange={handleQuickSelect}>
                            <SelectTrigger className="w-full text-sm">
                              <SelectValue placeholder="Quick select" />
                            </SelectTrigger>
                            <SelectContent position="popper" align="start">
                              <SelectItem value="1d">Last 1 day</SelectItem>
                              <SelectItem value="7d">Last 7 days</SelectItem>
                              <SelectItem value="month">
                                Current month
                              </SelectItem>
                              <SelectItem value="year">Current year</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-xs">Display</Label>
                          <div className="flex items-center rounded-md border overflow-hidden">
                            <Button
                              type="button"
                              variant={
                                displayMode === "time" ? "default" : "ghost"
                              }
                              size="sm"
                              className="rounded-none h-9 flex-1"
                              onClick={() => setDisplayMode("time")}
                            >
                              Time
                            </Button>
                            <Button
                              type="button"
                              variant={
                                displayMode === "percent" ? "default" : "ghost"
                              }
                              size="sm"
                              className="rounded-none h-9 flex-1"
                              onClick={() => setDisplayMode("percent")}
                            >
                              %
                            </Button>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs">Site</Label>
                        <Select
                          value={filters.siteName || "__all__"}
                          onValueChange={(value) =>
                            updateFilter(
                              "siteName",
                              value === "__all__" ? "" : value,
                            )
                          }
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="All Sites" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__all__">All Sites</SelectItem>
                            {sites?.map((site) => (
                              <SelectItem key={site.id} value={site.name}>
                                {site.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Area…"
                          value={filters.areaName || ""}
                          onChange={(e) =>
                            updateFilter("areaName", e.target.value)
                          }
                          className="h-9"
                        />
                        <Input
                          placeholder="Preset…"
                          value={filters.presetName || ""}
                          onChange={(e) =>
                            updateFilter("presetName", e.target.value)
                          }
                          className="h-9"
                        />
                        <Input
                          placeholder="Group…"
                          value={filters.groupName || ""}
                          onChange={(e) =>
                            updateFilter("groupName", e.target.value)
                          }
                          className="h-9"
                        />
                        <Input
                          placeholder="User…"
                          value={filters.userName || ""}
                          onChange={(e) =>
                            updateFilter("userName", e.target.value)
                          }
                          className="h-9"
                        />
                      </div>

                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={clearFilters}
                      >
                        Clear filters
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Desktop: collapsible filter bar (3 lines) */}
              <div className="hidden sm:block w-full">
                <div className="flex items-center justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={() => setFiltersExpanded((v) => !v)}
                  >
                    <Filter className="mr-2 h-4 w-4" />
                    {filtersExpanded ? "Hide filters" : "Show filters"}
                  </Button>
                </div>

                {filtersExpanded && (
                  <div className="mt-2 rounded-md border bg-muted/10 p-3">
                  {/* 1) Date related */}
                  <div className="flex items-center gap-3">
                    <div className="w-[140px] shrink-0 flex items-center">
                      <div className="text-xs font-medium text-muted-foreground">
                        Date
                      </div>
                    </div>
                    <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-8 w-[160px] justify-start text-left font-normal text-sm"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {format(startDate, "PP")}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={startDate}
                            onSelect={(d) => d && setStartDate(d)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <span className="text-muted-foreground text-sm">→</span>

                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="h-8 w-[160px] justify-start text-left font-normal text-sm"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                            <span className="truncate">
                              {format(endDate, "PP")}
                            </span>
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={endDate}
                            onSelect={(d) => d && setEndDate(d)}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>

                      <Select onValueChange={handleQuickSelect}>
                        <SelectTrigger className="h-8 w-[150px] text-sm">
                          <SelectValue placeholder="Quick select" />
                        </SelectTrigger>
                        <SelectContent position="popper" align="start">
                          <SelectItem value="1d">Last 1 day</SelectItem>
                          <SelectItem value="7d">Last 7 days</SelectItem>
                          <SelectItem value="month">Current month</SelectItem>
                          <SelectItem value="year">Current year</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="my-3 border-t" />

                  {/* 2) Site / Area / Preset */}
                  <div className="flex items-center gap-3">
                    <div className="w-[140px] shrink-0 flex items-center">
                      <div className="text-xs font-medium text-muted-foreground">
                        Site / Area / Preset
                      </div>
                    </div>
                    <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
                      <Select
                        value={filters.siteName || "__all__"}
                        onValueChange={(value) =>
                          updateFilter(
                            "siteName",
                            value === "__all__" ? "" : value
                          )
                        }
                      >
                        <SelectTrigger className="h-8 w-[180px]">
                          <SelectValue placeholder="All Sites" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">All Sites</SelectItem>
                          {sites?.map((site) => (
                            <SelectItem key={site.id} value={site.name}>
                              {site.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-[200px] justify-between"
                          >
                            <MultiSelectTriggerLabel
                              placeholder="Area"
                              allLabel="All Areas"
                              selectedCount={selectedAreaNames.length}
                            />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-[260px]">
                          <DropdownMenuLabel>Area</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuCheckboxItem
                            checked={selectedAreaNames.length === 0}
                            onCheckedChange={() => setMulti("areaName", [])}
                          >
                            All Areas
                          </DropdownMenuCheckboxItem>
                          <DropdownMenuSeparator />
                          {(usageNameOptions?.areaNames ?? []).map((name) => (
                            <DropdownMenuCheckboxItem
                              key={name}
                              checked={selectedAreaNames.includes(name)}
                              onCheckedChange={() =>
                                toggleMultiValue("areaName", name)
                              }
                            >
                              {name}
                            </DropdownMenuCheckboxItem>
                          ))}
                          {(usageNameOptions?.areaNames?.length ?? 0) === 0 && (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              No options
                            </div>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-[200px] justify-between"
                          >
                            <MultiSelectTriggerLabel
                              placeholder="Preset"
                              allLabel="All Presets"
                              selectedCount={selectedPresetNames.length}
                            />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-[260px]">
                          <DropdownMenuLabel>Preset</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuCheckboxItem
                            checked={selectedPresetNames.length === 0}
                            onCheckedChange={() => setMulti("presetName", [])}
                          >
                            All Presets
                          </DropdownMenuCheckboxItem>
                          <DropdownMenuSeparator />
                          {(usageNameOptions?.presetNames ?? []).map((name) => (
                            <DropdownMenuCheckboxItem
                              key={name}
                              checked={selectedPresetNames.includes(name)}
                              onCheckedChange={() =>
                                toggleMultiValue("presetName", name)
                              }
                            >
                              {name}
                            </DropdownMenuCheckboxItem>
                          ))}
                          {(usageNameOptions?.presetNames?.length ?? 0) === 0 && (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              No options
                            </div>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  <div className="my-3 border-t" />

                  {/* 3) Group / User */}
                  <div className="flex items-center gap-3">
                    <div className="w-[140px] shrink-0 flex items-center">
                      <div className="text-xs font-medium text-muted-foreground">
                        Group / User
                      </div>
                    </div>
                    <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-[200px] justify-between"
                          >
                            <MultiSelectTriggerLabel
                              placeholder="Group"
                              allLabel="All Groups"
                              selectedCount={selectedGroupNames.length}
                            />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-[260px]">
                          <DropdownMenuLabel>Group</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuCheckboxItem
                            checked={selectedGroupNames.length === 0}
                            onCheckedChange={() => setMulti("groupName", [])}
                          >
                            All Groups
                          </DropdownMenuCheckboxItem>
                          <DropdownMenuSeparator />
                          {(groups ?? [])
                            .map((g) => g.name)
                            .sort((a, b) => a.localeCompare(b))
                            .map((name) => (
                              <DropdownMenuCheckboxItem
                                key={name}
                                checked={selectedGroupNames.includes(name)}
                                onCheckedChange={() =>
                                  toggleMultiValue("groupName", name)
                                }
                              >
                                {name}
                              </DropdownMenuCheckboxItem>
                            ))}
                          {(groups?.length ?? 0) === 0 && (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              No options
                            </div>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 w-[240px] justify-between"
                          >
                            <MultiSelectTriggerLabel
                              placeholder="User"
                              allLabel="All Users"
                              selectedCount={selectedUserNames.length}
                            />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-[320px]">
                          <DropdownMenuLabel>User</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuCheckboxItem
                            checked={selectedUserNames.length === 0}
                            onCheckedChange={() => setMulti("userName", [])}
                          >
                            All Users
                          </DropdownMenuCheckboxItem>
                          <DropdownMenuSeparator />
                          {(users ?? [])
                            .map((u) => u.email)
                            .sort((a, b) => a.localeCompare(b))
                            .map((email) => (
                              <DropdownMenuCheckboxItem
                                key={email}
                                checked={selectedUserNames.includes(email)}
                                onCheckedChange={() =>
                                  toggleMultiValue("userName", email)
                                }
                              >
                                {email}
                              </DropdownMenuCheckboxItem>
                            ))}
                          {(users?.length ?? 0) === 0 && (
                            <div className="px-2 py-1.5 text-sm text-muted-foreground">
                              No options
                            </div>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>

                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8"
                        onClick={clearFilters}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  </div>
                )}
              </div>
            </div>
                        
            {/* Title + tab/display toggles */}
            <div className="pt-3 mt-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="text-lg font-semibold">
                {activeTab === "data" ? "Usage Details" : "Usage Summary"}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {activeTab === "data" && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={exportCsv}
                    disabled={isExportingCsv}
                  >
                    {isExportingCsv ? "Exporting…" : "Export CSV"}
                  </Button>
                )}

                <TabsList className="rounded-md border overflow-hidden bg-transparent p-0">
                  <TabsTrigger
                    value="summary"
                    className="rounded-none h-8 data-[state=inactive]:bg-transparent"
                  >
                    Summary
                  </TabsTrigger>
                  <TabsTrigger
                    value="data"
                    className="rounded-none h-8 data-[state=inactive]:bg-transparent"
                  >
                    Data
                  </TabsTrigger>
                </TabsList>

                <div className="flex items-center rounded-md border overflow-hidden">
                  <Button
                    type="button"
                    variant={displayMode === "time" ? "default" : "ghost"}
                    size="sm"
                    className="rounded-none h-8"
                    onClick={() => setDisplayMode("time")}
                  >
                    Time
                  </Button>
                  <Button
                    type="button"
                    variant={displayMode === "percent" ? "default" : "ghost"}
                    size="sm"
                    className="rounded-none h-8"
                    onClick={() => setDisplayMode("percent")}
                  >
                    %
                  </Button>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            <TabsContent value="summary" className="mt-4">
              {sitesLoading ? (
                <div className="text-muted-foreground">Loading...</div>
              ) : sites && sites.length > 0 ? (
                <UsageSummaryTable
                  sites={sites}
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  filters={filters}
                  dateRangeParams={dateRangeParams}
                  showHeaderFilters={false}
                  embedded
                  displayMode={displayMode}
                />
              ) : (
                <div className="text-muted-foreground">No sites available</div>
              )}
            </TabsContent>
            <TabsContent value="data" className="mt-4">
              <UsageDetailsTable
                filters={filters}
                dateRangeParams={dateRangeParams}
                displayMode={displayMode}
                embedded
              />
            </TabsContent>
          </CardContent>
        </Card>
      </Tabs>
    </div>
  );
}
